import React, { useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  Building2,
  Check,
  FileText,
  Folder,
  FolderOpen,
  Loader2,
  Lock,
  User,
} from 'lucide-react';
import { Modal, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { formatFileSize } from '../../../components/attachments/fileIconUtils';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX } from '../../../lib/zLayers';
import api from '../../../services/api';
import {
  uploadTemplateDocumentApi,
  type EmailTemplateDocument,
  type EmailTemplateFolder,
} from '../useData';
import type { EmailAttachment } from '../utils/attachments';
import {
  findLibraryDocumentByName,
  sanitizeLibraryDocumentName,
  suggestLibraryDocumentRename,
} from '../utils/libraryDocumentNames';
import SaveAttachmentConflictModal, {
  type AttachmentConflictChoice,
} from './SaveAttachmentConflictModal';

const LIBRARY_DOCUMENT_EXTENSIONS = new Set(['.pdf', '.doc', '.docx', '.xls', '.xlsx']);

export function isLibraryDocumentFilename(filename: string): boolean {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return false;
  return LIBRARY_DOCUMENT_EXTENSIONS.has(filename.slice(dot).toLowerCase());
}

type FolderTreeNode = EmailTemplateFolder & { children: FolderTreeNode[] };
type FolderPickerOption = { id: number; name: string; depth: number; locked: boolean };

function isFolderPathLocked(folderId: number, folders: EmailTemplateFolder[]): boolean {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  let current: number | null = folderId;
  while (current != null) {
    const folder = byId.get(current);
    if (!folder) break;
    if (folder.isLocked) return true;
    current = folder.parentId;
  }
  return false;
}

function buildFolderTree(folders: EmailTemplateFolder[]): FolderTreeNode[] {
  const selectable = folders.filter((folder) => !folder.isSystemRoot);
  const byId = new Map<number, FolderTreeNode>();
  for (const folder of selectable) {
    byId.set(folder.id, { ...folder, children: [] });
  }
  const roots: FolderTreeNode[] = [];
  for (const node of Array.from(byId.values())) {
    if (node.parentId != null && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortNodes = (nodes: FolderTreeNode[]) => {
    nodes.sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
    nodes.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);
  return roots;
}

function flattenFolderPicker(
  nodes: FolderTreeNode[],
  allFolders: EmailTemplateFolder[],
  depth = 0,
): FolderPickerOption[] {
  const out: FolderPickerOption[] = [];
  for (const node of nodes) {
    out.push({
      id: node.id,
      name: node.name,
      depth,
      locked: isFolderPathLocked(node.id, allFolders),
    });
    if (node.children.length) {
      out.push(...flattenFolderPicker(node.children, allFolders, depth + 1));
    }
  }
  return out;
}

function buildFolderSections(
  companyFolders: EmailTemplateFolder[],
  personalFolders: EmailTemplateFolder[],
  personalTemplates: { folderId?: number | null; folder?: { id: number; name: string; parentId: number | null } | null }[],
  canSaveCompany: boolean,
): { label: string; scope: 'COMPANY' | 'PERSONAL'; options: FolderPickerOption[] }[] {
  const sections: { label: string; scope: 'COMPANY' | 'PERSONAL'; options: FolderPickerOption[] }[] = [];
  if (canSaveCompany) {
    const companyOptions = flattenFolderPicker(buildFolderTree(companyFolders), companyFolders);
    if (companyOptions.length > 0) {
      sections.push({ label: 'Company', scope: 'COMPANY', options: companyOptions });
    }
  }

  const personalFoldersMerged = mergePersonalTemplateFolders(personalFolders, personalTemplates);
  const personalOptions = flattenFolderPicker(
    buildFolderTree(personalFoldersMerged),
    personalFoldersMerged,
  );
  sections.push({ label: 'Personal', scope: 'PERSONAL', options: personalOptions });
  return sections;
}

function mergePersonalTemplateFolders(
  folders: EmailTemplateFolder[],
  templates: { folderId?: number | null; folder?: { id: number; name: string; parentId: number | null } | null }[],
): EmailTemplateFolder[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  for (const template of templates) {
    if (template.folderId != null && template.folder && !byId.has(template.folderId)) {
      byId.set(template.folderId, {
        id: template.folder.id,
        name: template.folder.name,
        parentId: template.folder.parentId,
        displayOrder: 999,
      });
    }
  }
  return Array.from(byId.values());
}

interface SaveAttachmentsToLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  canSaveCompany: boolean;
  personalFolders: EmailTemplateFolder[];
  personalTemplates: { folderId?: number | null; folder?: { id: number; name: string; parentId: number | null } | null }[];
  companyFolders: EmailTemplateFolder[];
  personalDocuments: EmailTemplateDocument[];
  companyDocuments: EmailTemplateDocument[];
  emailId: number;
  partitionQuery: string;
  attachments: EmailAttachment[];
  onSaved: (result: { saved: number; skipped: number }) => void;
}

type ConflictPrompt = {
  filename: string;
  suggestedName: string;
  resolve: (choice: AttachmentConflictChoice | null) => void;
};

const SaveAttachmentsToLibraryModal: React.FC<SaveAttachmentsToLibraryModalProps> = ({
  isOpen,
  onClose,
  canSaveCompany,
  personalFolders,
  personalTemplates,
  companyFolders,
  personalDocuments,
  companyDocuments,
  emailId,
  partitionQuery,
  attachments,
  onSaved,
}) => {
  const { colors } = useTheme();
  const wasOpenRef = useRef(false);
  const [selectedAttachmentIndices, setSelectedAttachmentIndices] = useState<Set<number>>(new Set());
  const [folderId, setFolderId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictPrompt, setConflictPrompt] = useState<ConflictPrompt | null>(null);
  const [folderDocuments, setFolderDocuments] = useState<EmailTemplateDocument[]>([]);

  const attachmentRows = useMemo(
    () =>
      attachments.map((attachment, index) => ({
        index,
        attachment,
        filename: attachment.filename || `attachment-${index + 1}`,
        supported: isLibraryDocumentFilename(attachment.filename || ''),
      })),
    [attachments],
  );

  const supportedIndices = useMemo(
    () => attachmentRows.filter((row) => row.supported).map((row) => row.index),
    [attachmentRows],
  );

  const folderSections = useMemo(
    () => buildFolderSections(companyFolders, personalFolders, personalTemplates, canSaveCompany),
    [companyFolders, personalFolders, personalTemplates, canSaveCompany],
  );

  const folderOptions = useMemo(
    () => folderSections.flatMap((section) => section.options),
    [folderSections],
  );

  const unlockedFolderOptions = useMemo(
    () => folderOptions.filter((folder) => !folder.locked),
    [folderOptions],
  );

  const selectedFolder = useMemo(
    () => (folderId != null ? folderOptions.find((folder) => folder.id === folderId) ?? null : null),
    [folderId, folderOptions],
  );

  const selectedSupportedCount = useMemo(
    () => supportedIndices.filter((index) => selectedAttachmentIndices.has(index)).length,
    [supportedIndices, selectedAttachmentIndices],
  );

  const allSupportedSelected =
    supportedIndices.length > 0 && supportedIndices.every((index) => selectedAttachmentIndices.has(index));

  React.useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened) return;
    setSelectedAttachmentIndices(new Set(supportedIndices));
    setFolderId(unlockedFolderOptions[0]?.id ?? null);
    setError(null);
    setSaving(false);
    setConflictPrompt(null);
    setFolderDocuments([...companyDocuments, ...personalDocuments]);
  }, [isOpen, supportedIndices, unlockedFolderOptions, companyDocuments, personalDocuments]);

  React.useEffect(() => {
    if (!isOpen || folderId == null || selectedFolder?.locked !== true) return;
    setFolderId(unlockedFolderOptions[0]?.id ?? null);
  }, [isOpen, folderId, selectedFolder?.locked, unlockedFolderOptions]);

  const toggleAttachment = (index: number, supported: boolean) => {
    if (!supported || saving) return;
    setSelectedAttachmentIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (saving) return;
    setSelectedAttachmentIndices(allSupportedSelected ? new Set() : new Set(supportedIndices));
  };

  const finishAndClose = (saved: number, skipped: number) => {
    onClose();
    onSaved({ saved, skipped });
  };

  const askConflictChoice = (filename: string, suggestedName: string): Promise<AttachmentConflictChoice | null> =>
    new Promise((resolve) => {
      setConflictPrompt({ filename, suggestedName, resolve });
    });

  const uploadWithConflictHandling = async (
    targetFolderId: number,
    initialFile: File,
    documents: EmailTemplateDocument[],
  ): Promise<{ outcome: 'saved' | 'skipped' | 'cancelled'; savedName?: string; documents: EmailTemplateDocument[] }> => {
    let file = initialFile;
    let workingDocuments = documents;

    while (true) {
      const folderDocs = workingDocuments.filter((document) => document.folderId === targetFolderId);
      const existing = findLibraryDocumentByName(workingDocuments, targetFolderId, file.name);
      if (!existing) {
        const result = await uploadTemplateDocumentApi(targetFolderId, file, {
          ifExists: 'fail',
          name: sanitizeLibraryDocumentName(file.name),
        });
        if ('skipped' in result) {
          return { outcome: 'skipped', documents: workingDocuments };
        }
        const nextDocuments = [
          ...workingDocuments.filter(
            (document) => !(document.folderId === targetFolderId && document.name === result.name),
          ),
          result,
        ];
        return { outcome: 'saved', savedName: result.name, documents: nextDocuments };
      }

      const suggestedName = suggestLibraryDocumentRename(file.name, folderDocs);
      const choice = await askConflictChoice(file.name, suggestedName);
      setConflictPrompt(null);
      if (!choice) {
        return { outcome: 'cancelled', documents: workingDocuments };
      }
      if (choice.action === 'skip') {
        return { outcome: 'skipped', documents: workingDocuments };
      }
      if (choice.action === 'overwrite') {
        const result = await uploadTemplateDocumentApi(targetFolderId, file, { ifExists: 'overwrite' });
        if ('skipped' in result) {
          return { outcome: 'skipped', documents: workingDocuments };
        }
        const nextDocuments = [
          ...workingDocuments.filter((document) => document.id !== existing.id),
          result,
        ];
        return { outcome: 'saved', savedName: result.name, documents: nextDocuments };
      }

      file = new File([file], choice.name, { type: file.type });
    }
  };

  const handleSubmit = async () => {
    if (folderId == null || selectedSupportedCount === 0 || selectedFolder?.locked) return;
    setSaving(true);
    setError(null);
    let saved = 0;
    let skipped = 0;
    let documents = [...folderDocuments];
    try {
      for (const row of attachmentRows) {
        if (!selectedAttachmentIndices.has(row.index)) continue;
        if (!row.supported) {
          skipped++;
          continue;
        }
        const { data } = await api.get(
          `/emails/${emailId}/attachments/${row.index}${partitionQuery}`,
          { responseType: 'blob' },
        );
        const file = new File([data as Blob], row.filename, {
          type: row.attachment.contentType || 'application/octet-stream',
        });
        const { outcome, documents: nextDocuments } = await uploadWithConflictHandling(folderId, file, documents);
        documents = nextDocuments;
        setFolderDocuments(documents);
        if (outcome === 'cancelled') {
          if (saved > 0) {
            finishAndClose(saved, skipped);
          } else {
            setError('Save cancelled.');
          }
          return;
        }
        if (outcome === 'skipped') {
          skipped++;
          continue;
        }
        saved++;
      }
      if (saved === 0) {
        setError(
          skipped > 0
            ? 'All selected files were skipped or unsupported.'
            : 'No supported attachments could be saved. Library accepts PDF, Word, and Excel only.',
        );
        return;
      }
      finishAndClose(saved, skipped);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      if (saved > 0) {
        finishAndClose(saved, skipped);
        return;
      }
      setError(message || 'Failed to save attachments to library.');
    } finally {
      setSaving(false);
      setConflictPrompt(null);
    }
  };

  const handleConflictClose = () => {
    conflictPrompt?.resolve(null);
    setConflictPrompt(null);
  };

  const handleConflictChoose = (choice: AttachmentConflictChoice) => {
    conflictPrompt?.resolve(choice);
  };

  const handleClose = () => {
    if (saving || conflictPrompt) return;
    onClose();
  };

  return (
    <>
      <SaveAttachmentConflictModal
        isOpen={conflictPrompt != null}
        filename={conflictPrompt?.filename ?? ''}
        folderName={selectedFolder?.name ?? 'folder'}
        suggestedName={conflictPrompt?.suggestedName ?? ''}
        onClose={handleConflictClose}
        onChoose={handleConflictChoose}
      />
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" zIndex={Z_INDEX.modalHigh} closeOnEscape={!saving && conflictPrompt == null}>
      <div
        className={cn(
          'flex shrink-0 items-start gap-4 border-b px-6 py-5',
          colors.border,
          'bg-gradient-to-r from-indigo-50/90 via-white to-violet-50/50',
          'dark:from-indigo-950/30 dark:via-gray-950 dark:to-violet-950/20',
        )}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
          <BookOpen className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className={cn('text-lg font-semibold', colors.text)}>Save attachments to library</h2>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            Pick files on the left, choose a folder on the right, then save.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          disabled={saving}
          className={cn('rounded-lg p-1.5', colors.textMuted, colors.interactiveHover, saving && 'opacity-50')}
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid min-h-[24rem] grid-cols-1 lg:grid-cols-2 lg:divide-x">
          {/* Attachments */}
          <section className="flex min-h-0 flex-col">
            <div className={cn('flex items-center justify-between gap-3 border-b px-4 py-3', colors.border)}>
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-900/60 dark:text-slate-300">
                  <FileText className="h-3.5 w-3.5" aria-hidden />
                </span>
                <div>
                  <p className={cn('text-sm font-semibold', colors.text)}>Attachments</p>
                  <p className={cn('text-[11px]', colors.textMuted)}>PDF, Word, Excel</p>
                </div>
              </div>
              {supportedIndices.length > 0 ? (
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  disabled={saving}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs font-medium',
                    colors.textMuted,
                    colors.interactiveHover,
                  )}
                >
                  {allSupportedSelected ? 'Clear' : 'Select all'}
                </button>
              ) : null}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-1.5 scrollbar-thin">
              {attachmentRows.length === 0 ? (
                <p className={cn('px-2 py-8 text-sm text-center', colors.textMuted)}>No attachments</p>
              ) : (
                attachmentRows.map((row) => {
                  const checked = selectedAttachmentIndices.has(row.index);
                  return (
                    <label
                      key={row.index}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-all',
                        colors.border,
                        row.supported
                          ? checked
                            ? 'border-indigo-300 bg-indigo-50 shadow-sm dark:border-indigo-800 dark:bg-indigo-950/30'
                            : 'bg-white hover:border-gray-300 dark:bg-gray-950/40 dark:hover:border-gray-600'
                          : 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-gray-900/30',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={checked}
                        disabled={!row.supported || saving}
                        onChange={() => toggleAttachment(row.index, row.supported)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm font-medium truncate', colors.text)} title={row.filename}>
                          {row.filename}
                        </p>
                        <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                          {formatFileSize(row.attachment.size)}
                          {!row.supported ? ' · Unsupported format' : ''}
                        </p>
                      </div>
                      {checked && row.supported ? (
                        <Check className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />
                      ) : null}
                    </label>
                  );
                })
              )}
            </div>
          </section>

          {/* Library */}
          <section className="flex min-h-0 flex-col">
            <div className={cn('flex items-center gap-2 border-b px-4 py-3', colors.border)}>
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-600 dark:bg-violet-950/50 dark:text-violet-300">
                <Folder className="h-3.5 w-3.5" aria-hidden />
              </span>
              <div>
                <p className={cn('text-sm font-semibold', colors.text)}>Library folder</p>
                <p className={cn('text-[11px]', colors.textMuted)}>Where copies will be stored</p>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin">
              {folderSections.length === 0 ? (
                <p className={cn('px-2 py-8 text-sm text-center', colors.textMuted)}>
                  Create a folder in the library first.
                </p>
              ) : (
                folderSections.map((section) => (
                  <div key={section.label} className="space-y-1">
                    <div className="flex items-center gap-2 px-1 py-1">
                      {section.scope === 'COMPANY' ? (
                        <Building2 className="h-3.5 w-3.5 text-indigo-500" aria-hidden />
                      ) : (
                        <User className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
                      )}
                      <p className={cn('text-[11px] font-bold uppercase tracking-wider', colors.textMuted)}>
                        {section.label}
                      </p>
                    </div>
                    {section.options.length === 0 ? (
                      <p className={cn('px-3 py-2 text-xs leading-relaxed', colors.textMuted)}>
                        No personal folders yet. Create one in the Library under Personal.
                      </p>
                    ) : (
                      section.options.map((folder) => {
                      const selected = folderId === folder.id;
                      return (
                        <button
                          key={folder.id}
                          type="button"
                          disabled={saving || folder.locked}
                          onClick={() => setFolderId(folder.id)}
                          title={folder.locked ? 'Folder is locked' : undefined}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium transition-all',
                            folder.locked && 'cursor-not-allowed opacity-50',
                            selected && !folder.locked
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : cn(
                                  colors.text,
                                  !folder.locked && 'hover:bg-gray-100 dark:hover:bg-gray-900/50',
                                ),
                          )}
                          style={{ paddingLeft: 12 + folder.depth * 14 }}
                        >
                          {selected && !folder.locked ? (
                            <FolderOpen className="h-4 w-4 shrink-0" aria-hidden />
                          ) : (
                            <Folder className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                          )}
                          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                          {folder.locked ? (
                            <span title="Locked" className="inline-flex shrink-0">
                              <Lock className="h-3.5 w-3.5" aria-hidden />
                            </span>
                          ) : selected ? (
                            <Check className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                          ) : null}
                        </button>
                      );
                    })
                    )}
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        {error ? (
          <div className="mx-4 mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        ) : null}
      </div>

      <ModalFooter className="!justify-between gap-4">
        <div className={cn('min-w-0 text-xs', colors.textMuted)}>
          <span className={cn('font-medium', colors.text)}>
            {selectedSupportedCount} file{selectedSupportedCount === 1 ? '' : 's'} selected
          </span>
          {selectedFolder && !selectedFolder.locked ? (
            <>
              <span className="mx-1.5">→</span>
              <span className={cn('truncate font-medium', colors.text)}>{selectedFolder.name}</span>
            </>
          ) : (
            <span className="ml-1.5">· Pick a folder</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Close
          </Button>
          <Button
            variant="default"
            onClick={() => void handleSubmit()}
            disabled={
              saving ||
              folderId == null ||
              selectedSupportedCount === 0 ||
              unlockedFolderOptions.length === 0 ||
              selectedFolder?.locked === true
            }
            className="min-w-[7.5rem]"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              `Save${selectedSupportedCount > 0 ? ` (${selectedSupportedCount})` : ''}`
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default SaveAttachmentsToLibraryModal;
